<template>
	<div>
		<CRow v-if="title" class="mb-4">
			<CCol lg="12">
				<h4 class="m-0">
					{{ title }}
				</h4>
			</CCol>
		</CRow>
		<CRow class="mb-4">
			<CCol lg="12 typo-label">
				{{ $t('message.set_rule_by') }}*
			</CCol>
			<CCol lg="12">
				<CButtonGroup>
					<CButton
						v-for="({ text, value, color }, index) in transformTabs"
						:key="index"
						:color="color"
						:disabled="disabled"
						@click="onClickTab($event, value)"
					>
						{{ text }}
					</CButton>
				</CButtonGroup>
			</CCol>
		</CRow>
		<CRow>
			<CCol lg="6">
				<label>
					{{ $t(buttonLabelText) }}
				</label>
			</CCol>
			<CCol v-if="!disabled" lg="6" class="text-right">
				<CButton
					color="secondary"
					class="transparent"
					@click="$refs[currentModalName].open()"
				>
					{{ $t(buttonOpenModalText) }}
				</CButton>
			</CCol>
			<template>
				<CCol
					v-if="isTabCategory"
					md="12"
					class="mt-3 mb-4"
				>
					<div v-if="selectedCategories.length">
						<ul class="items-preview-box">
							<li
								v-for="category in selectedCategories"
								:key="category.id"
								class="category-item typo-body-2"
							>
								<span
									v-for="(path, index) in category.pathName"
									:key="index"
									class="category-name"
								>
									{{ path }}
								</span>
							</li>
						</ul>
						<p class="typo-label m-0">
							{{ $t('message.selected_total', { value: selectedCategories.length }) }}
						</p>
					</div>
					<BaseNoItemBanner
						v-else
						:text="$t('message.there_is_no_category_for_this_rule')"
						class="empty-box"
					/>
				</CCol>
				<CCol
					v-if="isTabSku"
					md="12"
					class="mt-3 mb-4"
				>
					<BaseNoItemBanner
						v-if="selectedSkusAll"
						:text="$t('message.selectedSkuAll', { value: selectedSkusCountFriendly })"
						class="empty-box"
					/>
					<div v-else-if="selectedSkus.length">
						<ul class="items-preview-box">
							<li
								v-for="(sku, index) in limitSelectedSkus"
								:key="index"
								class="category-item typo-body-2"
							>
								{{ sku }}
							</li>
							<li
								v-if="selectedSkus.length > 22"
								class="category-item category-item-add-more typo-body-2"
							>
								…and more
							</li>
						</ul>
						<p class="typo-label">
							{{ $t('message.selected_total', { value: selectedSkusCountFriendly }) }}
						</p>
					</div>
					<BaseNoItemBanner
						v-else
						:text="$t('message.thereIsNoSkuForThisRule')"
						class="empty-box"
					/>
				</CCol>
			</template>
			<template v-if="!disabled">
				<CCol lg="6">
					<label>
						{{ $t('message.excludeSku') }}
					</label>
				</CCol>
				<CCol v-if="!disabled" lg="6" class="text-right">
					<CButton
						color="secondary"
						class="transparent"
						@click="$refs['modal-exclude-sku'].open()"
					>
						{{ $t(buttonExcludeSkuText) }}
					</CButton>
				</CCol>
				<CCol
					md="12"
					class="mt-3 mb-4"
				>
					<BaseNoItemBanner
						v-if="selectedExcludeSkusAll"
						:text="$t('message.thereIsNoExcludeSkuForThisRule', { value: selectedExcludeSkusCountFriendly })"
						class="empty-box"
					/>
					<div v-else-if="selectedExcludeSkus.length">
						<ul class="items-preview-box">
							<li
								v-for="(sku, index) in selectedExcludeSkus"
								:key="index"
								class="category-item typo-body-2"
							>
								{{ sku }}
							</li>
							<li
								v-if="selectedExcludeSkus.length > 22"
								class="category-item category-item-add-more typo-body-2"
							>
								…and more
							</li>
						</ul>
						<p class="typo-label">
							{{ $t('message.selected_total', { value: selectedExcludeSkusCountFriendly }) }}
						</p>
					</div>
					<BaseNoItemBanner
						v-else
						:text="$t('message.thereIsNoExcludeSkuForThisRule')"
						class="empty-box"
					/>
				</CCol>
			</template>
			<CCol
				v-if="isValid === false"
				class="mt-1"
			>
				<div class="invalid-feedback d-block">
					{{ $t('message.categories_or_skus_is_required') }}
				</div>
			</CCol>
		</CRow>

		<!-- Modal Categoty -->
		<ModalCategory
			ref="modal-category"
			:title="$t('message.available_for_categories')"
			:selected-ids="selectedCategoryIds"
			@onUpdateCategories="handleUpdateCategories"
			@onSubmit="handleCategorySubmit"
		/>
		<!-- End Modal Categoty -->

		<!-- Modal SKU -->
		<ModalSku
			ref="modal-sku"
			:title="$t('message.selectSkus')"
			:selected-ids="selectedSkus"
			:check-all-skus="selectedSkusAll"
			@onSubmit="handleSkuSubmit"
		/>
		<!-- End Modal SKU -->

		<!-- Modal Exclude SKU -->
		<ModalSku
			ref="modal-exclude-sku"
			:title="$t('message.selectSkus')"
			:selected-ids="selectedExcludeSkus"
			:check-all-skus="selectedExcludeSkusAll"
			@onSubmit="handleExcludeSkuSubmit"
		/>
		<!-- End Modal Exclude SKU -->
	</div>
</template>

<script>
import useSelectSkuCategory from '../use/selectSkuCategory';
import { ALLOCATION_BY } from '../enums/allocation';

export default {
	name: 'SelectSkuCategory',

	props: {
		title: {
			type: String,
			default: null,
		},
		type: {
			type: String,
			default: ALLOCATION_BY.CATEGOTY,
		},
		values: {
			type: Array,
			default: () => [],
		},
		excludeValues: {
			type: Array,
			default: () => [],
		},
		isValid: {
			type: Boolean,
			default: null,
		},
		checkAll: {
			type: Boolean,
			default: false,
		},
		disabled: {
			type: Boolean,
			default: false,
		},
	},

	setup(props, context) {
		const options = {
			defaultState: {
				selectedType: props.type,
				selectedValues: props.values,
				selectedExcludeValues: props.excludeValues,
				selectedCheckAll: props.checkAll,
			},
		};

		const {
			tabs,
			transformTabs,
			selectedCategories,
			selectedCategoryIds,
			selectedSkus,
			selectedSkusAll,
			selectedExcludeSkusAll,
			selectedSkusCount,
			selectedSkusCountFriendly,
			selectedExcludeSkus,
			selectedExcludeSkusCount,
			selectedExcludeSkusCountFriendly,
			selectedCheckAll,
			limitSelectedSkus,
			isShowMore,
			currentModalName,
			buttonLabelText,
			isTabCategory,
			isTabSku,
			buttonOpenModalText,
			buttonExcludeSkuText,
			handleUpdateCategories,
			handleCategorySubmit,
			handleSkuSubmit,
			handleExcludeSkuSubmit,
			onClickTab,
			onClickOpenModal,
		} = useSelectSkuCategory(props, context, options);

		return {
			tabs,
			transformTabs,
			selectedCategories,
			selectedCategoryIds,
			selectedSkus,
			selectedSkusAll,
			selectedExcludeSkusAll,
			selectedSkusCount,
			selectedSkusCountFriendly,
			selectedExcludeSkus,
			selectedExcludeSkusCount,
			selectedExcludeSkusCountFriendly,
			selectedCheckAll,
			limitSelectedSkus,
			isShowMore,
			currentModalName,
			buttonLabelText,
			isTabCategory,
			isTabSku,
			buttonOpenModalText,
			buttonExcludeSkuText,
			handleUpdateCategories,
			handleCategorySubmit,
			handleSkuSubmit,
			handleExcludeSkuSubmit,
			onClickTab,
			onClickOpenModal,
		};
	},
};
</script>

<style lang="scss" scoped>
.empty-box {
	&.is-invalid {
		border: 1px solid $color-alert;
		border-radius: rem(4);
	}
}

.items-preview-box {
	overflow-y: auto;
	min-height: rem(60);
	max-height: rem(468);
	padding: rem(20) rem(16);
	border-radius: rem(4);
	border: solid 1px $color-gray-400;
	list-style: none;
}

.category-item {
	border-radius: rem(4);
	background-color: $color-gray-300;
	color: $color-black-90;
	display: inline-block;
	padding: 0 rem(6);
	margin-right: rem(12);
	margin-bottom: rem(12);

	.category-name {
		color: $color-black-45;

		&::after {
			content: "/";
			display: inline-block;
		}

		&:last-child {
			color: $color-black-90;
			font-weight: 600;

			&::after {
				display: none;
			}
		}
	}
}

.category-item-add-more {
	background: none;
}
</style>
